import React from 'react';

interface SkeletonProps {
  className?: string;
  width?: string;
  height?: string;
  rounded?: boolean;
  shimmer?: boolean;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  width = 'w-full',
  height = 'h-4',
  rounded = false,
  shimmer = true,
}) => {
  return (
    <div
      className={`
        ${width} ${height}
        bg-gray-200 dark:bg-gray-700
        ${rounded ? 'rounded-full' : 'rounded'}
        ${shimmer ? 'shimmer' : ''}
        ${className}
      `}
    />
  );
};

export const SkeletonCard: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <div className={`p-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 ${className}`}>
      <div className="flex items-center space-x-4 mb-4">
        <Skeleton width="w-12" height="h-12" rounded />
        <div className="flex-1">
          <Skeleton width="w-3/4" height="h-4" className="mb-2" />
          <Skeleton width="w-1/2" height="h-3" />
        </div>
      </div>
      <Skeleton width="w-full" height="h-3" className="mb-2" />
      <Skeleton width="w-5/6" height="h-3" className="mb-2" />
      <Skeleton width="w-4/6" height="h-3" />
    </div>
  );
};

export const SkeletonTable: React.FC<{ rows?: number; columns?: number }> = ({
  rows = 5,
  columns = 4,
}) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Table Header */}
      <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
        <div className="flex space-x-4">
          {Array.from({ length: columns }).map((_, index) => (
            <Skeleton
              key={index}
              width={`w-${Math.floor(24 / columns)}`}
              height="h-4"
              className="flex-1"
            />
          ))}
        </div>
      </div>
      
      {/* Table Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div
          key={rowIndex}
          className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 last:border-b-0"
        >
          <div className="flex space-x-4">
            {Array.from({ length: columns }).map((_, colIndex) => (
              <Skeleton
                key={colIndex}
                width={`w-${Math.floor(24 / columns)}`}
                height="h-3"
                className="flex-1"
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export const SkeletonChart: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <div className={`p-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 ${className}`}>
      <div className="flex justify-between items-center mb-6">
        <Skeleton width="w-1/3" height="h-6" />
        <Skeleton width="w-20" height="h-8" />
      </div>
      
      {/* Chart Area */}
      <div className="h-64 flex items-end space-x-2">
        {Array.from({ length: 8 }).map((_, index) => (
          <Skeleton
            key={index}
            width="w-full"
            height={`h-${Math.floor(Math.random() * 40) + 20}`}
            className="flex-1"
          />
        ))}
      </div>
      
      {/* Legend */}
      <div className="flex justify-center space-x-6 mt-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="flex items-center space-x-2">
            <Skeleton width="w-3" height="h-3" rounded />
            <Skeleton width="w-16" height="h-3" />
          </div>
        ))}
      </div>
    </div>
  );
};

export const SkeletonForm: React.FC<{ fields?: number }> = ({ fields = 4 }) => {
  return (
    <div className="space-y-6">
      {Array.from({ length: fields }).map((_, index) => (
        <div key={index}>
          <Skeleton width="w-1/4" height="h-4" className="mb-2" />
          <Skeleton width="w-full" height="h-10" />
        </div>
      ))}
      
      <div className="flex space-x-4 pt-4">
        <Skeleton width="w-24" height="h-10" />
        <Skeleton width="w-20" height="h-10" />
      </div>
    </div>
  );
};

export const SkeletonStats: React.FC<{ count?: number }> = ({ count = 4 }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <Skeleton width="w-16" height="h-3" className="mb-2" />
              <Skeleton width="w-20" height="h-8" />
            </div>
            <Skeleton width="w-10" height="h-10" rounded />
          </div>
          <Skeleton width="w-24" height="h-3" className="mt-4" />
        </div>
      ))}
    </div>
  );
};

export const SkeletonList: React.FC<{ items?: number }> = ({ items = 5 }) => {
  return (
    <div className="space-y-3">
      {Array.from({ length: items }).map((_, index) => (
        <div
          key={index}
          className="flex items-center space-x-3 p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
        >
          <Skeleton width="w-10" height="h-10" rounded />
          <div className="flex-1">
            <Skeleton width="w-3/4" height="h-4" className="mb-1" />
            <Skeleton width="w-1/2" height="h-3" />
          </div>
          <Skeleton width="w-16" height="h-6" />
        </div>
      ))}
    </div>
  );
};